import React, { useState, useEffect } from "react";
import { Form, Button, Alert } from "react-bootstrap";
import Select from "react-select";
import axios from "axios";

import { REACT_APP_SERVER_WAREHOUSE_SERVER_BASE_URL } from "config";

const baseUrl = REACT_APP_SERVER_WAREHOUSE_SERVER_BASE_URL;

function idStr(v) {
  if (v == null) return null;
  return typeof v === "string" ? v : v.toString();
}

const BaseProductMetadataEditor = ({ baseProductId, onSaved }) => {
  const [categories, setCategories] = useState([]);
  const [specFields, setSpecFields] = useState([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [specValues, setSpecValues] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoaded(false);
      setError("");
      try {
        const [catRes, fieldsRes, bpRes] = await Promise.all([
          axios.get(`${baseUrl}/api/admin/categories?includeInactive=1`),
          axios.get(`${baseUrl}/api/admin/spec-fields?includeOptions=1`),
          axios.get(`${baseUrl}/api/admin/base-products/${baseProductId}`),
        ]);
        if (cancel) {
          return;
        }
        setCategories(catRes.data.categories || []);
        setSpecFields(fieldsRes.data.specFields || []);
        const baseProduct = bpRes.data.baseProduct;
        const meta = baseProduct && baseProduct.baseProduct;
        if (!meta) {
          setSelectedCategoryIds([]);
          setSpecValues({});
        } else {
          setSelectedCategoryIds(
            (meta.categoryIds || [])
              .map((id) => idStr(id))
              .filter(Boolean)
          );
          const sv = { ...(meta.specValues || {}) };
          const out = {};
          for (const [k, v] of Object.entries(sv)) {
            if (v == null) {
              continue;
            }
            if (Array.isArray(v)) {
              out[k] = v.map((x) => idStr(x)).filter(Boolean);
            } else if (typeof v === "object" && v._id) {
              out[k] = idStr(v._id);
            } else {
              out[k] = v;
            }
          }
          setSpecValues(out);
        }
        setLoaded(true);
      } catch (e) {
        if (cancel) {
          return;
        }
        setError(
          "Could not load product metadata. Are you signed in as admin?"
        );
        setLoaded(true);
        console.log(e);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [baseProductId]);

  const categoryOptions = categories.map((c) => ({
    value: c._id,
    label: c.active ? c.name : `${c.name} (inactive)`,
  }));

  const selectedCategoryValue = selectedCategoryIds
    .map((id) => {
      const c = categories.find((x) => idStr(x._id) === id);
      return c ? { value: c._id, label: c.name } : null;
    })
    .filter(Boolean);

  const setSpec = (key, value) => {
    setSpecValues((prev) => {
      const next = { ...prev };
      if (value === null || value === "" || (Array.isArray(value) && value.length === 0)) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        categoryIds: selectedCategoryIds,
        specValues,
      };
      await axios.put(
        `${baseUrl}/api/admin/base-products/${baseProductId}`,
        payload
      );
      if (onSaved) {
        onSaved();
      }
    } catch (e) {
      setError(e.response?.data?.msg || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const fieldOptionChoices = (field) => {
    const options = (field.options || []).filter((o) => o.active !== false);
    return options.map((o) => ({
      value: o._id,
      label: o.label,
    }));
  };

  if (!loaded) {
    return <p>Loading product metadata…</p>;
  }

  return (
    <div>
      <h4>Marketing categories &amp; hardware specs</h4>
      <p className="text-muted" style={{ fontSize: 14, maxWidth: 720 }}>
        Assigns MongoDB metadata merged onto this BigCommerce product. Options are
        defined under Admin → Base product metadata; for catalog products, use
        Admin → Products (this editor appears only for registered base products).
      </p>
      {error && <Alert variant="danger">{String(error)}</Alert>}
      <Form.Group controlId="metadataCategories">
        <Form.Label>Categories</Form.Label>
        <Select
          isMulti
          options={categoryOptions}
          value={selectedCategoryValue}
          onChange={(list) => {
            setSelectedCategoryIds(
              (list || []).map((o) => idStr(o.value))
            );
          }}
          isClearable
        />
      </Form.Group>
      {specFields.map((field) => {
        const k = field.key;
        if (field.valueKind === "single") {
          const choices = fieldOptionChoices(field);
          const v = specValues[k];
          const value =
            (choices || []).find((o) => idStr(o.value) === idStr(v)) || null;
          return (
            <Form.Group key={k} controlId={`spec-${k}`}>
              <Form.Label>{field.label}</Form.Label>
              <Select
                options={choices}
                value={value}
                onChange={(opt) => setSpec(k, opt ? idStr(opt.value) : null)}
                isClearable
              />
            </Form.Group>
          );
        }
        if (field.valueKind === "multi") {
          const choices = fieldOptionChoices(field);
          const v = specValues[k];
          const arr = Array.isArray(v)
            ? v
            : v != null && v !== ""
              ? [v]
              : [];
          const value = arr
            .map((id) => choices.find((o) => idStr(o.value) === idStr(id)))
            .filter(Boolean);
          return (
            <Form.Group key={k} controlId={`specm-${k}`}>
              <Form.Label>{field.label}</Form.Label>
              <Select
                isMulti
                options={choices}
                value={value}
                onChange={(list) => {
                  setSpec(
                    k,
                    (list || []).map((o) => idStr(o.value))
                  );
                }}
                isClearable
              />
            </Form.Group>
          );
        }
        if (field.valueKind === "number") {
          const n = specValues[k];
          return (
            <Form.Group key={k} controlId={`specn-${k}`}>
              <Form.Label>{field.label}</Form.Label>
              <Form.Control
                type="number"
                value={n == null || n === "" ? "" : n}
                onChange={(e) => {
                  const t = e.target.value;
                  if (t === "") {
                    setSpec(k, null);
                    return;
                  }
                  const x = parseFloat(t, 10);
                  setSpec(k, Number.isNaN(x) ? null : x);
                }}
              />
            </Form.Group>
          );
        }
        if (field.valueKind === "text") {
          return (
            <Form.Group key={k} controlId={`spect-${k}`}>
              <Form.Label>{field.label}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={specValues[k] == null ? "" : String(specValues[k])}
                onChange={(e) => {
                  setSpec(
                    k,
                    e.target.value === "" ? null : e.target.value
                  );
                }}
              />
            </Form.Group>
          );
        }
        return null;
      })}
      <Button variant="primary" onClick={save} disabled={saving}>
        {saving ? "Saving…" : "Save categories & specs"}
      </Button>
    </div>
  );
};

export default BaseProductMetadataEditor;
